import jsyaml from 'js-yaml'
import { generateSlug } from './hooks/useBuilderPages'

/**
 * Le CHEMIN PUBLIC des contenus d'un type — sa grammaire, côté écran.
 *
 * Miroir de `ModelPathTrait` (web/modules/custom/core/orbit_builder/src/Controller).
 * Le VERDICT reste celui de Drupal : ce module existe pour dire l'erreur à la
 * frappe et pour montrer l'adresse qu'un contenu aura, pas pour décider.
 *
 * Un chemin est un motif : des segments littéraux, et UN segment qui désigne le
 * contenu.
 *
 *  - `@<clé>` — la valeur du CHAMP `<clé>`, mise en forme d'URL :
 *    `/actualites/@titre` sert « Ma Belle Actualité » à
 *    `/actualites/ma-belle-actualite`. Plusieurs jetons tiennent dans le même
 *    segment, avec du texte littéral entre eux (`/emplois/@intitule-@ville`).
 *  - `:slug` — la forme HÉRITÉE : le segment est saisi à la main sur chaque
 *    contenu, rien n'est dérivé de ses champs.
 *
 * Un seul segment dynamique, parce que c'est lui qu'on stocke : la valeur
 * composée devient le slug du contenu.
 */

/** Un jeton `@champ`. Le `g` est posé à chaque usage — un regex global garde un état. */
const TOKEN = () => /@([a-zA-Z_][a-zA-Z0-9_]*)/g

/**
 * Minuscules, accents retirés, ponctuation retirée, espaces et tirets fondus.
 * Mêmes règles que `slugify()` / `generateSlug()` — un titre donne la même
 * adresse par quelque chemin qu'on y arrive.
 */
const collapse = (text) =>
  String(text)
    .toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/[\s-]+/g, '-')
    .replace(/^-|-$/g, '')

/**
 * Met une valeur de champ en forme d'URL. Une valeur non scalaire (référence,
 * liste, objet) ne fait pas une adresse : elle rend la chaîne vide.
 */
export const urlizeValue = (value) => {
  if (value === null || value === undefined || typeof value === 'object' || typeof value === 'boolean') return ''
  return collapse(String(value).trim())
}

/** Les segments d'un chemin, sans les slashs de tête ni de queue. */
export const modelPathSegments = (path) => {
  const trimmed = String(path || '').replace(/^\/+|\/+$/g, '')
  return trimmed === '' ? [] : trimmed.split('/')
}

/** Ce segment désigne-t-il le contenu ? */
export const isDynamicSegment = (segment) =>
  segment !== '' && (segment[0] === ':' || segment.includes('@'))

/**
 * L'index du segment qui désigne le contenu — `-1` quand il n'y en a AUCUN ou
 * plusieurs (les deux cas sont refusés par `validateModelPath`, seul endroit
 * qui a un message à en dire).
 */
export const dynamicSegmentIndex = (path) => {
  let found = -1
  modelPathSegments(path).forEach((segment, i) => {
    if (!isDynamicSegment(segment)) return
    found = found === -1 ? i : -2
  })
  return found < 0 ? -1 : found
}

/** Les clés de champ citées par le chemin, dans l'ordre et sans doublon. */
export const modelPathFieldKeys = (path) =>
  [...new Set([...String(path || '').matchAll(TOKEN())].map(m => m[1]))]

/** Le chemin est-il construit depuis les champs (`@`) plutôt que saisi (`:slug`) ? */
export const isDerivedPath = (path) => {
  const index = dynamicSegmentIndex(path)
  return index !== -1 && modelPathSegments(path)[index].includes('@')
}

/**
 * Vérifie un chemin. Renvoie un message d'erreur, ou `null`.
 *
 * @param {string} path Le motif saisi.
 * @param {string[]} knownKeys Les clés de champ du type. Vide ⇒ les clés
 *   citées ne sont pas vérifiées (schéma illisible : on ne refuse pas pour ça).
 */
export const validateModelPath = (path, knownKeys = []) => {
  const value = String(path || '')
  if (value === '' || value[0] !== '/') return 'Le chemin doit commencer par « / ».'
  const dynamic = modelPathSegments(value).filter(isDynamicSegment)
  if (dynamic.length === 0) {
    return "Le chemin doit désigner le contenu : écrivez @ suivi de la clé d'un de ses champs (ex. /actualites/@titre)."
  }
  if (dynamic.length > 1) {
    return `Un seul segment du chemin peut désigner le contenu ; celui-ci en compte ${dynamic.length} (${dynamic.join(', ')}).`
  }
  const segment = dynamic[0]
  if (segment[0] === ':') {
    return segment === ':slug'
      ? null
      : `« ${segment} » ne désigne aucun contenu : écrivez @ suivi de la clé d'un champ (ex. @titre).`
  }
  const keys = modelPathFieldKeys(segment)
  if (keys.length === 0) {
    return `Le segment « ${segment} » ne nomme aucun champ : écrivez @ suivi de la clé du champ (ex. @titre).`
  }
  if (knownKeys.length > 0) {
    const unknown = keys.filter(k => !knownKeys.includes(k))
    if (unknown.length > 0) {
      return `Ce type de contenu n'a pas de champ « ${unknown.join(' », « ')} ». Champs disponibles : ${knownKeys.join(', ')}.`
    }
  }
  return null
}

/**
 * Compose le segment d'adresse d'un contenu depuis ses champs. `''` quand le
 * chemin n'est pas dérivé, ou qu'aucun des champs cités n'est renseigné — à
 * l'appelant de retomber sur ce qu'il affichait déjà.
 */
export const composeModelSlug = (path, data = {}) => {
  if (!isDerivedPath(path)) return ''
  const segment = modelPathSegments(path)[dynamicSegmentIndex(path)]
  const filled = segment.replace(TOKEN(), (_, key) => urlizeValue(data?.[key]))
  const slug = collapse(filled)
  // Le segment ne vaut que par les CHAMPS : réduit à son seul texte littéral
  // (tous les jetons vides), il donnerait la même adresse à tous les contenus.
  const literal = collapse(segment.replace(TOKEN(), ''))
  return slug === '' || slug === literal ? '' : slug
}

/**
 * Le chemin, son segment dynamique remplacé :
 * `/actualites/@titre` + `ma-belle` → `/actualites/ma-belle`.
 */
export const fillModelPath = (path, slug) => {
  const index = dynamicSegmentIndex(path)
  if (index === -1) return String(path || '')
  const segments = modelPathSegments(path)
  segments[index] = slug
  return '/' + segments.join('/')
}

/**
 * Ce qu'un contenu affichera comme adresse : le chemin rempli par ses champs,
 * à défaut par le slug qu'il porte déjà, à défaut par un exemple.
 */
export const previewModelPath = (path, { data, slug, sample = 'mon-contenu' } = {}) => {
  if (!path) return ''
  const derived = data ? composeModelSlug(path, data) : ''
  return fillModelPath(path, derived || slug || sample)
}

// ---------------------------------------------------------------------------
// Ce qui ALIMENTE le chemin : le schéma du type. Ici plutôt que dans le
// composant du champ — un module qui exporte autre chose que des composants
// casse le rafraîchissement à chaud (règle eslint `react-refresh`).
// ---------------------------------------------------------------------------

/** Les champs déclarés par le schéma du type — `[{key, label}]`. */
export const fieldsOf = (definition) => {
  try {
    const parsed = jsyaml.load(definition || '') || {}
    return (Array.isArray(parsed.fields) ? parsed.fields : [])
      .filter(f => f && f.key)
      .map(f => ({ key: String(f.key), label: f.label ? String(f.label) : '' }))
  } catch {
    return []
  }
}

/** Le champ primaire du type — celui qui porte son titre. */
export const primaryFieldOf = (definition) => {
  try {
    const parsed = jsyaml.load(definition || '') || {}
    return parsed.primaryField ? String(parsed.primaryField) : ''
  } catch {
    return ''
  }
}

/**
 * Chemin proposé par défaut : `/actualite/@titre` pour un type « Actualité »
 * dont le champ primaire s'appelle `titre`. Sans champ primaire lisible, on
 * retombe sur la forme héritée — un segment saisi à la main.
 */
export const defaultModelPath = (name, definition) => {
  const primary = primaryFieldOf(definition)
  const segment = primary ? `@${primary}` : ':slug'
  return `/${generateSlug(name || 'contenu')}/${segment}`
}

// ---------------------------------------------------------------------------
// Le même `@`, dans les MÉTADONNÉES d'un modèle — miroir d'`interpolateMeta()`
// (MetaTagsTrait). Deux différences avec le chemin, et elles tiennent à ce
// qu'on fabrique : la valeur reste du TEXTE (accents, majuscules, espaces
// gardés ; le balisage tombe), et une valeur structurée est réduite à ce
// qu'elle a de lisible — l'URL d'une image, le nom d'un terme.
// ---------------------------------------------------------------------------

/** Ce qu'une valeur de champ a de lisible dans une balise. */
export const textualValue = (value) => {
  if (value === null || value === undefined || typeof value === 'boolean') return ''
  if (typeof value !== 'object') return String(value).trim()
  if (Array.isArray(value)) return ''
  for (const key of ['url', 'name', 'title', 'label', 'value']) {
    const inner = value[key]
    if (inner !== null && inner !== undefined && typeof inner !== 'object') return String(inner).trim()
  }
  return ''
}

/**
 * Remplit les jetons d'une balise avec les données d'un contenu. `''` quand
 * aucun champ n'a répondu (la balise retombe alors sur son défaut), comme côté
 * Drupal.
 */
export const interpolateMetaText = (text, data = {}) => {
  const value = String(text || '')
  if (!TOKEN().test(value)) return value
  const filled = value.replace(TOKEN(), (_, key) => textualValue(data?.[key]))
  const clean = filled.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim()
  const literal = value.replace(TOKEN(), '').replace(/\s+/g, ' ').trim()
  return clean === literal ? '' : clean
}
